import {
    endOfDay,
    endOfMonth,
    endOfQuarter,
    endOfYear,
    format,
    startOfDay,
    startOfMonth,
    startOfQuarter,
    startOfYear,
    subMonths,
    subQuarters,
    subYears,
} from 'date-fns';
import { omit } from 'lodash';
import { parseClearPeriodValue, parseDateRangeValue } from '../../pages/settlements/view/vendorBillItems/sidebar/utils';
import { exportCSVData } from '../common/exportCSVData';
import { DATE_FORMAT_CSV } from '../commonConst';
import { IFetchPageQuery } from '../IFetchQueryOptions';
import { IFetchAlertTrackQuery, IFetchAlertTrackUsersQuery } from './interface';
import { ITrackingAlert, ITrackingUser } from '../event/interface';
import { API } from '../api';

export const fetchTrackingAlerts = async (params: IFetchAlertTrackQuery, signal?: AbortSignal): Promise<ITrackingAlert[]> => {
    let { companyId = null, search, filter, sorter } = params;

    const path = `/alert/tracking?${new URLSearchParams({
        ...prepareOrder(sorter),
        ...prepareFilter(filter),
        ...(companyId && { companyId: companyId.toString() }),
        ...(search && { search }),
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        method: 'get',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();
    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || 'Cannot fetch alert tracks logs');
};

export const exportTrackingAlerts = async ({ companyId, sorter, filter, search }: IFetchAlertTrackQuery) => {
    const response = await API.fetch(
        `/alert/tracking?${new URLSearchParams({
            format: 'csv',
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(companyId && { companyId: companyId.toString() }),
            ...(search && { search: search }),
            ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
            ...prepareFilter(filter as Record<string, any>),
            ...prepareOrder(sorter),
        })}`,
        {
            headers: {
                'Content-Type': 'text/csv',
            },
        }
    );

    if (response.ok) {
        const respBody: string = await response.text();

        const period =
            filter?.start && filter?.end
                ? `${format(new Date(filter?.start), 'MM-dd-yyyy')}_${format(new Date(filter?.end), 'MM-dd-yyyy')}`
                : `${format(new Date(), DATE_FORMAT_CSV)}`;
        const filename = `events-tracking-alerts_${period}.csv`;
        exportCSVData(respBody, filename);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const fetchAlertTrackingUsers = async (params: IFetchAlertTrackUsersQuery, signal?: AbortSignal | null): Promise<ITrackingUser[]> => {
    const { companyId } = params;

    const path = `/alert/tracking/users?${new URLSearchParams({
        ...(companyId && { companyId: companyId.toString() }),
    })}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot get site utilities!`);
};

export const fetchAlertTrackingPrograms = async (signal?: AbortSignal | null): Promise<string[]> => {
    const path = `/filters/options/event-historical-programs`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot get alert tracking programs!`);
};

function prepareOrder(sorter: IFetchPageQuery['sorter']) {
    if (!sorter || !sorter.field || !sorter.order) {
        return {};
    }

    return { sortField: sorter.field, sortOrder: sorter.order };
}

function prepareFilter(filter: IFetchPageQuery['filter']): IFetchPageQuery['filter'] {
    const { start, end } = getDateRangeFromPeriod(filter?.period);

    return {
        ...omit(filter, 'period'),
        ...(start && { start: start.toISOString() }),
        ...(end && { end: end.toISOString() }),
    };
}

function getDateRangeFromPeriod(period?: string): { start: Date | null; end: Date | null } {
    const today = new Date();

    const clearPeriod = parseClearPeriodValue(period);

    switch (clearPeriod) {
        case 'current_month':
            return {
                start: startOfMonth(today),
                end: endOfMonth(today),
            };

        case 'previous_month':
            const priorMonthDate = subMonths(today, 1);
            return {
                start: startOfMonth(priorMonthDate),
                end: endOfMonth(priorMonthDate),
            };

        case 'current_quarter':
            return {
                start: startOfQuarter(today),
                end: endOfQuarter(today),
            };

        case 'previous_quarter': {
            const previousQuarterDate = subQuarters(today, 1);
            return {
                start: startOfQuarter(previousQuarterDate),
                end: endOfQuarter(previousQuarterDate),
            };
        }

        case 'year_to_date':
            return {
                start: startOfYear(today),
                end: today,
            };

        case 'previous_year': {
            const previousYearDate = subYears(today, 1);
            return {
                start: startOfYear(previousYearDate),
                end: endOfYear(previousYearDate),
            };
        }
        case 'custom_range':
            const dateRange = parseDateRangeValue(period);

            if (!dateRange) {
                return {
                    start: null,
                    end: null,
                };
            }

            // The incoming dates carry the selected calendar day in UTC. Re-read
            // that day in the local timezone so start/end map to the local
            // start/end of day (avoids the 23:59:59Z value rolling to next day).
            const startDay = new Date(dateRange.start.getUTCFullYear(), dateRange.start.getUTCMonth(), dateRange.start.getUTCDate());
            const endDay = new Date(dateRange.end.getUTCFullYear(), dateRange.end.getUTCMonth(), dateRange.end.getUTCDate());

            return {
                start: startOfDay(startDay),
                end: endOfDay(endDay),
            };

        case 'all':
        default:
            return {
                start: null,
                end: null,
            };
    }
}
